import { useRouter } from 'next/router';
import React from 'react';
import { Container } from '@/components/elements/Container';
import { LinkWithQuery } from '@/components/elements/LinkWithQuery';
import { ButtonGroup } from '@/components/landing/blocks';
import { Button } from '@/components/UI';
import { useTranslationContext } from '@/context/translation';
import { useMounted } from '@/hooks';
import { useTranslationWithLocale } from '@/hooks/useTranslationWithLocale';
import { GenerateLandingLinkService } from '@/services';
import { getCCPAAccess } from '@/helpers';
import { isPageLocalized } from '@/helpers/localizedPages';
import { footerInfo, smmIcons } from '@/data/footerData';
import { DEFAULT_LOCALE, VALID_LOCALES } from '@/constants/locales';
import LogoIcon from '@/public/icons/logo-white.svg';
import styles from './Footer.module.scss';

export const Footer = () => {
	const router = useRouter();
	const { locale, translations } = useTranslationContext();

	// Hydration-safe CCPA(US) gate: false on SSR + first client render, then
	// resolved from geolocation after mount so the "Do Not Sell" link only
	// appears for US users.
	const isCCPAUser = useMounted() && getCCPAAccess();

	// Get current path without locale
	const getCurrentPath = (): string => {
		if (typeof window === 'undefined') {
			return router.asPath || '/';
		}
		const pathSegments = router.asPath.split('/').filter(Boolean);
		// Remove locale from path if present
		const pathWithoutLocale = pathSegments
			.filter(segment => !VALID_LOCALES.includes(segment))
			.join('/');
		return pathWithoutLocale ? `/${pathWithoutLocale}` : '/';
	};

	const currentPath = getCurrentPath();
	const isLocalized = isPageLocalized(currentPath);

	// Use default locale for landing-footer on non-localized pages
	const footerLocale = isLocalized ? locale : DEFAULT_LOCALE;
	const { t } = useTranslationWithLocale(
		'landing-footer',
		footerLocale,
		translations?.['landing-footer'] as Record<string, unknown> | undefined,
	);
	const link = (href: string) => {
		if (href === '/web') {
			return GenerateLandingLinkService.webApp();
		}
		return href;
	};

	return (
		<footer className={styles['footer']}>
			<Container className={styles['footer__container']}>
				<section className={`row ${styles['footer__row']}`}>
					{footerInfo.map(({ id, title, data }) => (
						<nav
							key={id}
							className={styles['footer__column']}
							aria-labelledby={id}
						>
							<h2 id={id} className={styles['footer__title']}>
								{t(title)}
							</h2>
							{data
								.filter(
									item =>
										item.link !== '/dont-sell-my-personal-info' || isCCPAUser,
								)
								.map(item => {
									const solutionsColumn = id === 'solutions';
									const isReview = item.link.startsWith('/reviews');

									return (
										<LinkWithQuery
											key={item.title}
											href={link(item.link)}
											className={styles['footer__item']}
											target={
												isReview || solutionsColumn ? undefined : '_blank'
											}
										>
											<span className={styles['footer__item-text']}>
												{t(item.title)}
											</span>
										</LinkWithQuery>
									);
								})}
						</nav>
					))}
				</section>
				<section className={styles['footer__actions']}>
					<div className={styles['footer__column-smm']}>
						<h3 className={styles['footer__title']}>{t('follow')}</h3>
						<div className={styles['footer__smm']}>
							{smmIcons.map(item => (
								<Button
									key={item.icon}
									classes={{
										button: styles['footer__smm-button'],
										icon: styles['footer__action-icon'],
									}}
									as='link'
									href={item.link}
									Icon={item.icon}
									data-e2e='install-btn'
									target='_blank'
									aria-label={`Follow us on ${item.id}`}
								/>
							))}
						</div>
					</div>

					<div className={styles['footer__column-apps']}>
						<h3 className={styles['footer__title']}>{t('getApp')}</h3>
						<ButtonGroup
							preset='landing_outline'
							classes={{
								blockClass: styles['footer__apps'],
							}}
						/>
					</div>
				</section>
			</Container>

			<div className={styles['footer__divider']} />

			<Container className={styles['footer__container']}>
				<section
					className={`${styles['footer__row']} ${styles['footer__row_lowest']}`}
				>
					<div className={`${styles['footer__column_logo']}`}>
						<div className={styles['footer__logo']}>
							<LogoIcon className={`${styles['footer__icon']}`} />
						</div>
					</div>
					<div className={`${styles['footer__column_copyright']}`}>
						<p className={styles['footer__copyright']}>
							Lift© {new Date().getFullYear()} {t('rights')}
						</p>
					</div>
					<div className={`${styles['footer__column_legal']}`}>
						<p className={styles['footer__legal']}>{t('legal')}</p>
					</div>
				</section>
			</Container>
		</footer>
	);
};
